import React, { createContext, useContext, useCallback, useEffect, ReactNode } from 'react';
import { useDomainFetch } from './hooks/useDomainFetch';
import { useAuth } from './AuthContext';
import { useSettings } from './SettingsContext';
import { SystemStatus, SystemVersions, MemoryUsage } from '../types';
import { API_BASE_URL } from '../config/api';

// Base interface for service status values (for dynamic access by string key)
interface ServiceStatusBase {
  connected?: boolean;
  enabled?: boolean;
  status?: string;
  error?: string | null;
  version?: string;
  uptime?: number;
  memory?: MemoryUsage;
  provider?: string;
}

interface SystemStatusContextType {
  // Data
  systemStatus: SystemStatus | null;
  systemVersions: SystemVersions | null;

  // State
  loading: boolean;
  error: string | null;
  isStale: boolean;
  lastFetch: number | null;

  // Actions
  refetch: () => Promise<void>;

  // Domain-specific utilities (unchanged external API from old wrapper)
  isServiceHealthy: (serviceName: string) => boolean;
  getUnhealthyServices: () => string[];
  getOverallHealthStatus: () => 'healthy' | 'warning' | 'critical' | 'unknown';
  getServiceStatus: (serviceName: string) => ServiceStatusBase | null;
  isSystemOnline: () => boolean;
  getUptime: () => number | null;
  getSystemVersion: () => string | null;
}

export const SystemStatusContext = createContext<SystemStatusContextType | undefined>(undefined);
export type { SystemStatusContextType };

interface SystemStatusProviderProps {
  children: ReactNode;
}

export function SystemStatusProvider({ children }: SystemStatusProviderProps) {
  const { refreshToken, logout } = useAuth();
  const { settings } = useSettings();

  const getToken = useCallback(
    () => (typeof window !== 'undefined' ? localStorage.getItem('tb_token') : null),
    [],
  );

  const onUnauthorized = useCallback(
    async (makeRequest: () => Promise<Response>): Promise<Response> => {
      const refreshTokenValue =
        typeof window !== 'undefined' ? localStorage.getItem('tb_refresh_token') : null;
      if (refreshTokenValue) {
        const refreshSuccess = await refreshToken();
        if (refreshSuccess) {
          return makeRequest();
        } else {
          logout();
        }
      } else {
        logout();
      }
      return new Response(null, { status: 401 });
    },
    [refreshToken, logout],
  );

  // --- systemStatus ---
  const {
    state: systemStatusState,
    refetch: refetchSystemStatus,
  } = useDomainFetch<SystemStatus>({
    endpoint: `${API_BASE_URL}/api/system/status`,
    refreshInterval: settings.refreshIntervals.status,
    getToken,
    onUnauthorized,
    onSuccess: (raw) => {
      // The API wraps responses in { success, data } via sendSuccess()
      // useDomainFetch stores raw -- the context value unwraps below
      void raw;
    },
  });

  // --- systemVersions ---
  const {
    state: systemVersionsState,
    refetch: refetchSystemVersions,
  } = useDomainFetch<SystemVersions>({
    endpoint: `${API_BASE_URL}/api/system/versions`,
    refreshInterval: settings.refreshIntervals.status,
    getToken,
    onUnauthorized,
  });

  // Initial fetch on mount
  useEffect(() => {
    if (systemStatusState.isStale || !systemStatusState.lastFetch) {
      refetchSystemStatus();
    }
    if (systemVersionsState.isStale || !systemVersionsState.lastFetch) {
      refetchSystemVersions();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  // Re-fetch when stale
  useEffect(() => {
    if (systemStatusState.isStale && systemStatusState.lastFetch) {
      refetchSystemStatus();
    }
  }, [systemStatusState.isStale, systemStatusState.lastFetch, refetchSystemStatus]);

  useEffect(() => {
    if (systemVersionsState.isStale && systemVersionsState.lastFetch) {
      refetchSystemVersions();
    }
  }, [systemVersionsState.isStale, systemVersionsState.lastFetch, refetchSystemVersions]);

  const refetch = useCallback(async (): Promise<void> => {
    await Promise.all([refetchSystemStatus(true), refetchSystemVersions(true)]);
  }, [refetchSystemStatus, refetchSystemVersions]);

  // Extract the actual data -- the system status API wraps in { success, data }
  const systemStatusData = systemStatusState.data
    ? ((systemStatusState.data as unknown as { data?: SystemStatus }).data ?? systemStatusState.data)
    : null;

  // For systemVersions, merge in frontend version info (matches DataManagerContext.tsx fetchSystemVersions)
  const systemVersionsData: SystemVersions | null = (() => {
    const backendData = systemVersionsState.data;
    if (!backendData) return null;
    const frontendVersion = {
      name: 'Frontend UI',
      version: process.env.NEXT_PUBLIC_APP_VERSION || '29.7.0',
      status: 'running' as const,
      buildTime: process.env.NEXT_PUBLIC_BUILD_TIME,
      nodeVersion: process.env.NEXT_PUBLIC_NODE_VERSION,
      nextVersion: process.env.NEXT_PUBLIC_NEXT_VERSION,
      reactVersion: process.env.NEXT_PUBLIC_REACT_VERSION,
      framework: `Next.js ${process.env.NEXT_PUBLIC_NEXT_VERSION || 'unknown'}`,
      runtime: `React ${process.env.NEXT_PUBLIC_REACT_VERSION || 'unknown'}`,
    };
    return {
      ...backendData,
      components: {
        ...(backendData as unknown as { components?: Record<string, unknown> }).components,
        frontend: frontendVersion,
      },
    } as SystemVersions;
  })();

  // Domain utilities (same logic as old SystemStatusContext wrapper)

  const isServiceHealthy = useCallback((serviceName: string): boolean => {
    if (!systemStatusData?.services) return false;
    const services = systemStatusData.services as Record<string, ServiceStatusBase>;
    const serviceStatus = services[serviceName];
    if (!serviceStatus) return false;
    if (serviceStatus.connected !== undefined) return serviceStatus.connected === true;
    if (serviceStatus.enabled !== undefined) return serviceStatus.enabled === true;
    if (typeof serviceStatus === 'string') {
      return serviceStatus === 'healthy' || serviceStatus === 'online';
    }
    return false;
  }, [systemStatusData]);

  const getUnhealthyServices = useCallback((): string[] => {
    if (!systemStatusData?.services) return [];
    const services = systemStatusData.services as Record<string, ServiceStatusBase>;
    return Object.entries(services)
      .filter(([, serviceObj]) => {
        if (serviceObj.connected !== undefined) return !serviceObj.connected;
        if (serviceObj.enabled !== undefined) return !serviceObj.enabled;
        return false;
      })
      .map(([serviceName]) => serviceName);
  }, [systemStatusData]);

  const getOverallHealthStatus = useCallback((): 'healthy' | 'warning' | 'critical' | 'unknown' => {
    if (!systemStatusData) return 'unknown';
    if (systemStatusState.error) return 'critical';
    const unhealthyServices = getUnhealthyServices();
    const criticalServices = ['database', 'tbMcp'];
    if (unhealthyServices.some((s) => criticalServices.includes(s))) return 'critical';
    if (unhealthyServices.length > 0) return 'warning';
    return 'healthy';
  }, [systemStatusData, systemStatusState.error, getUnhealthyServices]);

  const getServiceStatus = useCallback((serviceName: string): ServiceStatusBase | null => {
    if (!systemStatusData?.services) return null;
    const services = systemStatusData.services as Record<string, ServiceStatusBase>;
    return services[serviceName] || null;
  }, [systemStatusData]);

  const isSystemOnline = useCallback((): boolean => {
    if (!systemStatusData) return false;
    return systemStatusData.mcp.connected && systemStatusData.thingsboard.connected;
  }, [systemStatusData]);

  const getUptime = useCallback((): number | null => {
    return systemStatusData?.services?.backend?.uptime || null;
  }, [systemStatusData]);

  const getSystemVersion = useCallback((): string | null => {
    return systemStatusData?.services?.backend?.version || null;
  }, [systemStatusData]);

  const loading = systemStatusState.loading || systemVersionsState.loading;
  const error = systemStatusState.error || systemVersionsState.error;
  const isStale = systemStatusState.isStale || systemVersionsState.isStale;
  const lastFetchValues = [systemStatusState.lastFetch, systemVersionsState.lastFetch].filter(
    (f): f is number => f !== null,
  );
  const lastFetch = lastFetchValues.length > 0 ? Math.min(...lastFetchValues) : null;

  const contextValue: SystemStatusContextType = {
    systemStatus: systemStatusData,
    systemVersions: systemVersionsData,
    loading,
    error,
    isStale,
    lastFetch,
    refetch,
    isServiceHealthy,
    getUnhealthyServices,
    getOverallHealthStatus,
    getServiceStatus,
    isSystemOnline,
    getUptime,
    getSystemVersion,
  };

  return (
    <SystemStatusContext.Provider value={contextValue}>
      {children}
    </SystemStatusContext.Provider>
  );
}

export function useSystemStatus(): SystemStatusContextType {
  const context = useContext(SystemStatusContext);
  if (context === undefined) {
    throw new Error('useSystemStatus must be used within a SystemStatusProvider');
  }
  return context;
}

export default SystemStatusProvider;
